import { Controller } from "@hotwired/stimulus";
import StimulusReflex from "stimulus_reflex";

// Autocomplete search for location records, backed by LocationFormSearchReflex.
//
// Supports two modes via Stimulus values (defaults preserve the original
// multi-select behavior used by appellations/_form):
//   - multiple: true  -> chips + repeated hidden inputs (selectedIds/selected targets)
//   - multiple: false -> single hidden input (selectedId target)
// The hidden-input name is configurable via the paramName value so the same
// controller can feed different forms (e.g. appellation[location_ids][] vs
// location_form[parent_id]).
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "selectedId",
    "selectedIds",
    "selected",
    "spinner",
  ];
  static values = {
    multiple: { type: Boolean, default: true },
    paramName: { type: String, default: "appellation[location_ids][]" },
  };

  connect() {
    StimulusReflex.register(this);
  }

  search() {
    const query = this.inputTarget.value.trim();
    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden");
      return;
    }

    this.element.dataset.query = query;

    this.showSpinner();
    this.resultsTarget.classList.remove("hidden");
    this.resultsTarget.classList.add("opacity-50");

    this.stimulate("LocationFormSearch#search");
  }

  beforeSearch() {
    this.resultsTarget.classList.add("opacity-50");
    this.showSpinner();
  }

  searchSuccess() {
    this.resultsTarget.classList.remove("opacity-50");
    this.hideSpinner();
  }

  searchError(error) {
    console.error("Location search error:", error);
    this.resultsTarget.classList.remove("opacity-50");
    this.resultsTarget.classList.add("hidden");
    this.hideSpinner();
  }

  select(event) {
    const locationId = event.currentTarget.dataset.locationId;
    const locationName = event.currentTarget.dataset.locationName;

    if (this.multipleValue) {
      this.addSelectedLocation(locationId, locationName);
      this.inputTarget.value = "";
      this.selectedIdsTarget.dispatchEvent(new Event("change"));
    } else {
      this.inputTarget.value = locationName;
      if (this.hasSelectedIdTarget) {
        this.selectedIdTarget.value = locationId;
        this.selectedIdTarget.dispatchEvent(new Event("change"));
      }
    }

    this.resultsTarget.classList.add("hidden");
  }

  removeLocation(event) {
    const locationId = event.currentTarget.dataset.locationId;

    event.currentTarget.closest("[data-location-item]").remove();

    const hiddenInput = this.selectedIdsTarget.querySelector(
      `input[value="${locationId}"]`
    );
    if (hiddenInput) {
      hiddenInput.remove();
    }

    this.selectedIdsTarget.dispatchEvent(new Event("change"));
  }

  addSelectedLocation(locationId, locationName) {
    // Avoid duplicates
    if (
      this.selectedIdsTarget.querySelector(`input[value="${locationId}"]`)
    ) {
      return;
    }

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = this.paramNameValue;
    hiddenInput.value = locationId;
    this.selectedIdsTarget.appendChild(hiddenInput);

    const selectedItem = document.createElement("div");
    selectedItem.className =
      "flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2";
    selectedItem.setAttribute("data-location-item", locationId);

    selectedItem.innerHTML = `
      <span class="text-sm font-medium text-indigo-900">${locationName}</span>
      <button type="button"
              class="text-indigo-600 hover:text-indigo-800 ml-2 transition-colors duration-200"
              data-action="click->location-form-search#removeLocation"
              data-location-id="${locationId}">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;

    this.selectedTarget.appendChild(selectedItem);
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden");
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden");
    }
  }

  receiveResults(html) {
    this.resultsTarget.innerHTML = html;
    this.resultsTarget.classList.remove("hidden");
  }
}
